import React from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import DeviceStatus from './DeviceStatus';
import FrequentlyAskedQuestion from './FrequentlyAskedQuestion';
import {
    List, ListItem, ListItemText, Link, Container, Typography, Box, Paper, TextField, MenuItem,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert
} from '@mui/material';
import { useAuthContext } from '../context/AuthContext';
import TrackList from './TrackList';


// Why an institution could not be created, in plain words
const createInstitutionErrors: { [key: string]: string } = {
    invalid_name: "Please enter a name using letters and numbers.",
    institution_exists: "An institution with this name already exists.",
    admin_user_exists: "An admin login for this name already exists. Choose a different name.",
    no_pin_available: "Could not find a free headset pin. Please try again.",
    not_allowed: "Only Soapbox staff can create institutions.",
    network_error: "Could not reach the server. Please try again.",
};

/**
 * Super admins only. Creates an institution: its record with a headset pin, and
 * the admin login the institution uses for this dashboard. The login details are
 * shown once, to pass on to the institution.
 */
const NewInstitutionDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    const { createInstitution, institutions } = useAuthContext();
    const [name, setName] = React.useState("");
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState("");
    const [result, setResult] = React.useState<any>(null);
    const [copied, setCopied] = React.useState(false);

    // Same clean up the server applies, so the user sees the final name before creating
    const cleanName = name.replace(/[^a-zA-Z0-9]+/g, ' ').trim();
    const adminUsername = 'admin_' + cleanName.replace(/[^A-Za-z0-9]+/g, '').toLowerCase();
    // A name already in use (ignoring case and spaces) cannot be used again. The
    // server enforces this too; checking here just says so before anything is sent.
    const nameKey = (value: string) => value.replace(/ /g, '').toLowerCase();
    const nameTaken = cleanName !== "" && institutions.some((existing) => nameKey(existing) === nameKey(cleanName));

    const close = () => {
        if (busy) return;
        setName(""); setError(""); setResult(null); setCopied(false);
        onClose();
    };

    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (cleanName === "") { setError(createInstitutionErrors.invalid_name); return; }
        if (nameTaken) { setError(createInstitutionErrors.institution_exists); return; }
        setBusy(true);
        setError("");
        const data = await createInstitution(cleanName);
        setBusy(false);
        if (data?.error) setError(createInstitutionErrors[data.error] ?? "Something went wrong and nothing was created.");
        else setResult(data);
    };

    const details = result ? [
        `Institution: ${result.institution.name}`,
        `Dashboard: ${window.location.origin}`,
        `Username: ${result.admin.username}`,
        `Temporary password: ${result.admin.temporaryPassword}`,
        `Headset pin: ${result.institution.pin}`,
    ].join('\n') : '';

    const copy = async () => {
        try { await navigator.clipboard.writeText(details); setCopied(true); } catch { setCopied(false); }
    };

    return (
        <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
            <DialogTitle>New institution</DialogTitle>
            {result ? (
                <>
                    <DialogContent>
                        <Alert severity="success" sx={{ mb: 2 }}>Institution created.</Alert>
                        <Box component="pre" sx={{ m: 0, p: 2, bgcolor: 'action.hover', borderRadius: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '0.95rem' }}>
                            {details}
                        </Box>
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            Copy these details now. The temporary password is not shown again, and it expires if
                            it is not used within 7 days. The admin chooses their own password the first time they sign in.
                        </Alert>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={copy}>{copied ? 'Copied' : 'Copy details'}</Button>
                        <Button onClick={close} variant="contained">Done</Button>
                    </DialogActions>
                </>
            ) : (
                <Box component="form" onSubmit={submit} noValidate>
                    <DialogContent>
                        <TextField label="Institution name" fullWidth autoFocus value={name} error={nameTaken}
                            onChange={(e) => setName(e.currentTarget.value)} disabled={busy} sx={{ mt: 1 }} />
                        {nameTaken && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                An institution called <strong>{institutions.find((existing) => nameKey(existing) === nameKey(cleanName))}</strong> already
                                exists. Choose a different name.
                            </Alert>
                        )}
                        {cleanName !== "" && !nameTaken && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                Will be created as <strong>{cleanName}</strong>, with the admin login <strong>{adminUsername}</strong> and
                                a new headset pin. Names can only contain letters, numbers and spaces.
                            </Typography>
                        )}
                        <Typography variant="body2" sx={{ minHeight: "24px", mt: 1, color: "red" }}>
                            {error}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={close} disabled={busy}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={busy || cleanName === "" || nameTaken}>
                            {busy ? 'Creating...' : 'Create institution'}
                        </Button>
                    </DialogActions>
                </Box>
            )}
        </Dialog>
    );
};

const devicesFAQ = [
    {
        question: 'How to setup a headset?',
        answer:
            <>
                <Typography variant="body1">
                    To setup your headset and start provisioning content.
                </Typography>
                <List>
                    <ListItem>
                        <ListItemText primary="Step 1" secondary="Make sure your headset is charged and turned on" />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Step 2" secondary={
                            <>
                                <Link href="https://www.meta.com/help/quest/articles/getting-started/getting-started-with-quest-2/connect-quest-to-wi-fi/">Connect your headset to the internet.</Link>
                            </>
                        } />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Step 3" secondary="Open the Soapbox app" />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Step 4" secondary="Login using your institution's pin in the app, and keep the headset turned on" />
                    </ListItem>
                </List>
                <Typography variant="body1">
                    After following these steps, your headset will be available for content provisioning.
                </Typography>
            </>
    },
    {
        question: 'How to make my headset available?',
        answer:
            <>
                <Typography variant="body1">
                    Headset are considered available when recently connected to the internet, meaning that content can be provisioned.
                    To make your headset available, you need to follow the steps below:
                </Typography>
                <List>
                    <ListItem>
                        <ListItemText primary="Step 1" secondary="Make sure your headset is charged and turned on" />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Step 2" secondary={
                            <>
                                <Link href="https://www.meta.com/help/quest/articles/getting-started/getting-started-with-quest-2/connect-quest-to-wi-fi/">Connect your headset to the internet</Link>
                            </>
                        } />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Step 3" secondary="Open the Soapbox app" />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Step 4" secondary="Login using your institution's pin in the app, and keep the headset turned on" />
                    </ListItem>
                </List>
                <Typography variant="body1">
                    After following these steps, your headset will be available for content provisioning.
                </Typography>
            </>
    },
    {
        question: 'Hardware support',
        answer:
            <>
                <Typography variant="body1">
                    Please reach out to our support team for any hardware-related issues from the support tab.
                </Typography>
            </>
    }
];

const tracksFAQ = [
    {
        question: 'What\'s the difference between Download and Visible?',
        answer:
            <>
                <List>
                    <ListItem>
                        <ListItemText primary="Download" secondary="Download the content to all your headsets as soon as they get online. This process takes time and we recommend using a fast internet connection. Please make sure your content is sucessfully provisioned to each headset after making changes." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Visible" secondary="This option allows you to hide and show the content on the app without having to remove it from the headset. This setting will take effect on each headset on app restart." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Settings available" secondary="Controls whether the settings menu is available in the Soapbox app on your headsets. It is off by default, so users cannot change settings on the device unless you turn it on." />
                    </ListItem>
                </List>
            </>
    }
];


export default function DashboardTab() {

    const { soapboxData, superUser, institutions, selectedInstitution, selectInstitution, institution } = useAuthContext();
    const [newInstitutionOpen, setNewInstitutionOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        if (soapboxData !== null) {
            setLoading(false);
        }
    }, [soapboxData]);

    return (
        loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 5 }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ mt: 2 }}>Loading, please wait...</Typography>
                <Table sx={{ mt: 2, width: '80%' }}>
                </Table>
            </Box>
        ) : (
            <Container component="main" maxWidth="lg" sx={{ mt: 4 }}>

                {superUser && (
                    <Paper sx={{ mb: 4, p: 2 }}>
                        <Typography variant="h6">Super admin</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Soapbox staff view. Choose the institution to open: everything below is that
                            institution's dashboard, and changes you save apply to it.
                        </Typography>
                        <TextField
                            select
                            label="Institution"
                            size="small"
                            value={institutions.includes(selectedInstitution) ? selectedInstitution : ''}
                            onChange={(e) => selectInstitution(e.target.value)}
                            sx={{ minWidth: 320 }}
                        >
                            {institutions.map((name) => (
                                <MenuItem key={name} value={name}>{name}</MenuItem>
                            ))}
                        </TextField>
                        <Button variant="outlined" onClick={() => setNewInstitutionOpen(true)} sx={{ ml: 2, height: 40 }}>
                            New institution
                        </Button>
                        <NewInstitutionDialog open={newInstitutionOpen} onClose={() => setNewInstitutionOpen(false)} />
                        {!institution && (
                            <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
                                No institution open. Choose one above.
                            </Typography>
                        )}
                    </Paper>
                )}

                <Box sx={{ mb: 4 }}>
                    <Typography variant="h2">Headsets</Typography>
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        Manage your soapbox headsets and track content provisioning in real time.
                    </Typography>
                    <DeviceStatus />
                    <Box sx={{ padding: 2 }}>
                        <FrequentlyAskedQuestion frequentlyAskedQuestions={devicesFAQ} />
                    </Box>
                </Box>

                <Box sx={{ mb: 4 }}>
                    <Typography variant="h2">Content provisioning</Typography>
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        Manage which content gets provisioned and visible on the Soapbox app for your users.
                    </Typography>
                    <TrackList />
                    <Box sx={{ padding: 2 }}>
                        <FrequentlyAskedQuestion frequentlyAskedQuestions={tracksFAQ} />
                    </Box>
                </Box>


            </Container>
        )
    );
}